
import React, { useEffect, useRef } from 'react';
import { Switch } from "../ui/switch";
import { CircleDollarSign, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

interface ToggleFundingButtonProps {
  showFunding: boolean;
  setShowFunding: (show: boolean) => void;
}

const ToggleFundingButton: React.FC<ToggleFundingButtonProps> = ({
  showFunding,
  setShowFunding
}) => {
  const hasInitializedRef = useRef(false);
  const toggleCountRef = useRef(0);
  const previousStateRef = useRef(showFunding);
  
  // Sync body class with showFunding prop on mount and when it changes
  useEffect(() => {
    console.log("ToggleFundingButton: showFunding changed to", showFunding);
    const wasToggled = previousStateRef.current !== showFunding;
    previousStateRef.current = showFunding;
    
    // Apply the class directly to the body for immediate visual effect
    if (showFunding) {
      document.body.classList.remove('hide-funding');
      console.log("ToggleFundingButton: Removed hide-funding class from body");
    } else {
      document.body.classList.add('hide-funding');
      console.log("ToggleFundingButton: Added hide-funding class to body");
    }
    
    // Mark that we've initialized the component
    hasInitializedRef.current = true;
    
    // Force reflow for special regions by temporarily adding and removing a class
    const mapContainers = document.querySelectorAll('.mapboxgl-canvas-container');
    mapContainers.forEach(container => {
      container.classList.add('funding-toggle-update');
      setTimeout(() => {
        container.classList.remove('funding-toggle-update');
      }, 50);
    });
    
    // Handle special region pins visibility
    const handleSpecialRegionPins = () => {
      // Show/hide pins based on funding state
      const specialRegionPins = document.querySelectorAll('.special-region-pin');
      specialRegionPins.forEach((pin) => {
        const pinEl = pin as HTMLElement;
        pinEl.style.display = showFunding ? 'none' : 'block';
      });
      
      // Ensure city pins in special regions are visible when funding is hidden
      const specialCityPins = document.querySelectorAll('.chiapas-pin, .hidalgo-pin');
      specialCityPins.forEach((pin) => {
        const pinEl = pin as HTMLElement;
        if (!showFunding) {
          pinEl.style.visibility = 'visible';
          pinEl.style.opacity = '1';
          pinEl.style.display = 'block';
        }
      });
      
      console.log(`Special region pins updated: ${specialRegionPins.length} pins, display: ${showFunding ? 'none' : 'block'}`);
    };
    
    // Call once immediately
    handleSpecialRegionPins();
    
    // And again after a short delay to ensure all pins are properly shown/hidden
    setTimeout(handleSpecialRegionPins, 100);
    
    // Force a DOM refresh for special region markers
    const specialMarkers = document.querySelectorAll('.region-funding-chiapas, .region-funding-hidalgo');
    specialMarkers.forEach(marker => {
      const originalDisplay = window.getComputedStyle(marker).getPropertyValue('display');
      (marker as HTMLElement).style.display = 'none';
      setTimeout(() => {
        (marker as HTMLElement).style.display = originalDisplay;
      }, 10);
    });
    
    // Add a data attribute to the body to track toggle count for debugging
    toggleCountRef.current++;
    document.body.setAttribute('data-funding-toggle-count', String(toggleCountRef.current));
    
    // CRITICAL: Force recreation of ALL funding labels when toggling back to visible
    if (wasToggled && showFunding) {
      console.log("ToggleFundingButton: Forcing recreation of ALL funding labels");
      
      // First make SURE hide-funding class is removed from body
      document.body.classList.remove('hide-funding');
      
      // Dispatch a custom event with high priority that FundingLabels will listen for
      const recreateEvent = new CustomEvent('recreate-funding-labels', { 
        detail: { showFunding, forceRecreate: true } 
      });
      document.dispatchEvent(recreateEvent);
      
      // Make all existing funding labels immediately visible with inline styles
      // that override any CSS rules
      const allFundingElements = document.querySelectorAll('.funding-label, .city-marker-funding, .region-funding-label, .mexico-region-funding');
      allFundingElements.forEach(el => {
        (el as HTMLElement).style.visibility = 'visible';
        (el as HTMLElement).style.display = 'block';
        (el as HTMLElement).style.opacity = '1';
        (el as HTMLElement).setAttribute('data-funding-visible', 'true');
      });
      
      // Force a reflow by adding then removing a class on the parent container
      document.body.classList.add('funding-toggle-update');
      setTimeout(() => {
        document.body.classList.remove('funding-toggle-update');
      }, 50);
      
      // Dispatch another recreation event after a delay for better reliability
      setTimeout(() => {
        const secondRecreateEvent = new CustomEvent('recreate-funding-labels', { 
          detail: { showFunding, forceRecreate: true } 
        });
        document.dispatchEvent(secondRecreateEvent);
      }, 200);
    }
  }, [showFunding]);
  
  const handleToggle = (checked: boolean) => {
    console.log("Switch toggled, changing funding from", showFunding, "to", checked);
    
    // FIXED: Correctly update body class based on the new state (checked)
    if (checked) {
      document.body.classList.remove('hide-funding');
      console.log("Toggle: Removed hide-funding class - funding should be visible");
    } else {
      document.body.classList.add('hide-funding');
      console.log("Toggle: Added hide-funding class - funding should be hidden");
    }
    
    // FIXED: Force a repaint of all funding elements with correct visibility
    const fundingElements = document.querySelectorAll('[data-marker-type="funding"]');
    fundingElements.forEach(el => {
      // Directly set inline styles to override CSS rules when toggling
      if (checked) {
        (el as HTMLElement).style.visibility = 'visible';
        (el as HTMLElement).style.display = 'block';
        (el as HTMLElement).style.opacity = '1';
      } else {
        (el as HTMLElement).style.visibility = 'hidden';
        (el as HTMLElement).style.display = 'none';
        (el as HTMLElement).style.opacity = '0';
      }
    });
    
    // Update the state in the parent component - this will trigger the useEffect
    setShowFunding(checked);
    
    // Handle special regions differently
    const specialRegionPins = document.querySelectorAll('.special-region-pin');
    specialRegionPins.forEach((pin) => {
      const pinEl = pin as HTMLElement;
      pinEl.style.display = checked ? 'none' : 'block';
    });
    
    // CRITICAL: If funding is being turned ON, force recreation of ALL markers
    if (checked) {
      // Dispatch an event to trigger recreation of funding markers
      const recreateEvent = new CustomEvent('recreate-funding-labels', { 
        detail: { showFunding: true, forceRecreate: true } 
      });
      document.dispatchEvent(recreateEvent);
      
      // Force all existing funding elements to be visible with inline styles
      const allFundingElements = document.querySelectorAll('.funding-label, .city-marker-funding, .region-funding-label, .mexico-region-funding, [data-marker-type="funding"]');
      allFundingElements.forEach(el => {
        (el as HTMLElement).style.visibility = 'visible';
        (el as HTMLElement).style.display = 'block';
        (el as HTMLElement).style.opacity = '1';
        (el as HTMLElement).setAttribute('data-funding-visible', 'true');
      });
      
      // We need a stronger approach - trigger a complete DOM refresh
      document.body.classList.add('force-show-funding');
      
      // Delay removing the force class to ensure the browser applies the styles
      setTimeout(() => {
        document.body.classList.remove('force-show-funding');
      }, 100);
    }
    
    // Force repaint of the map container
    const mapContainer = document.querySelector('.mapboxgl-canvas-container');
    if (mapContainer) {
      mapContainer.classList.add('funding-toggle-update');
      setTimeout(() => {
        mapContainer.classList.remove('funding-toggle-update');
      }, 50);
    }
  };
  
  return (
    <div 
      className={cn(
        "fixed right-3 top-24 z-[1000] bg-white/90 backdrop-blur-sm shadow-md",
        "border border-gray-200",
        "flex items-center gap-2 px-3 py-1.5 rounded-md",
        "transition-all duration-300"
      )}
    >
      <div className="flex items-center gap-2">
        {showFunding ? (
          <CircleDollarSign className="h-4 w-4 text-primary" />
        ) : (
          <EyeOff className="h-4 w-4 text-gray-500" />
        )}
        <span className="text-xs font-medium">
          {showFunding ? "Funding" : "Show Funding"}
        </span>
      </div>
      <Switch
        checked={showFunding} 
        onCheckedChange={handleToggle}
        data-funding-state={showFunding ? "visible" : "hidden"}
        className="data-[state=checked]:bg-primary"
      />
    </div>
  );
};

export default ToggleFundingButton;
